import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Play, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface GameSetupProps {
  onCreateGame: (playerName: string, maxPlayers: number, packId?: string) => void
  onJoinGame: (playerName: string, roomCode?: string) => void
  onBack: () => void
}

import VintageLayout from '../layout/VintageLayout'

// ... (previous code identical until component start)

const GameSetup: React.FC<GameSetupProps> = ({ onCreateGame, onJoinGame, onBack }) => {
  // ... (hooks and state identical) ...
  const { user } = useAuth()
  const [playerName, setPlayerName] = useState('')
  const [maxPlayers, setMaxPlayers] = useState(4)

  useEffect(() => {
    if (user && user.username) {
      setPlayerName(user.username)
    }
  }, [user])
  const [mode, setMode] = useState<'create' | 'join'>('create')
  const [roomCode, setRoomCode] = useState('')
  // Pack Selection State
  const [packs, setPacks] = useState<{ officials: any[], mine: any[] }>({ officials: [], mine: [] })
  const [selectedPackId, setSelectedPackId] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const response = await api.get('/packs')
        if (response.data) {
          setPacks(response.data)
          if (response.data.officials && response.data.officials.length > 0) {
            setSelectedPackId(response.data.officials[0]._id)
          }
        }
      } catch (error) {
        console.error('Error fetching packs:', error)
      }
    }
    fetchPacks()
  }, [])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!playerName.trim()) {
      newErrors.playerName = 'Le nom du joueur est obligatoire'
    } else if (playerName.trim().length < 2) {
      newErrors.playerName = 'Le nom doit contenir au moins 2 caractères'
    } else if (playerName.trim().length > 20) {
      newErrors.playerName = 'Le nom ne peut pas dépasser 20 caractères'
    }

    if (mode === 'join' && !roomCode.trim()) {
      newErrors.roomCode = 'Le code de partie est requis'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmitting) return
    if (!validateForm()) return

    setIsSubmitting(true)

    const trimmedName = playerName.trim()

    try {
      if (mode === 'create') {
        onCreateGame(trimmedName, maxPlayers, selectedPackId)
      } else {
        onJoinGame(trimmedName, roomCode.trim().toUpperCase())
      }
    } catch (error) {
      setIsSubmitting(false)
    } finally {
      setTimeout(() => setIsSubmitting(false), 2000)
    }
  }

  const getSelectedPack = () => {
    const official = packs.officials.find(p => p._id === selectedPackId)
    if (official) return official
    return packs.mine.find(p => p._id === selectedPackId)
  }

  return (
    <VintageLayout>
      <div className="h-screen flex flex-col font-serif relative z-10">
        {/* Header */}
        <header className="relative z-10 p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gold-100/70 hover:text-gold-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>

            <div className="text-center">
              <h1 className="text-4xl font-bold text-gold-500 drop-shadow-lg" style={{ fontFamily: 'Playfair Display, serif' }}>RÉMINISCENCE</h1>
              <p className="text-gold-100/60 text-sm tracking-widest uppercase mt-1">Configuration de Partie</p>
            </div>

            <div className="w-20"></div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
          <motion.div
            className="w-full max-w-lg bg-black/60 backdrop-blur-md border border-gold-500/30 p-8 rounded-3xl shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Mode Selection */}
            <div className="mb-8">
              <div className="flex rounded-xl overflow-hidden border border-gold-500/30">
                <button
                  onClick={() => setMode('create')}
                  className={`flex-1 py-3 px-4 text-sm font-semibold transition-all ${mode === 'create'
                    ? 'bg-gold-500 text-black shadow-[0_0_20px_rgba(234,179,8,0.3)]'
                    : 'bg-black/30 text-gold-100/70 hover:bg-gold-500/10'
                    }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Créer une Partie
                </button>
                <button
                  onClick={() => setMode('join')}
                  className={`flex-1 py-3 px-4 text-sm font-semibold transition-all ${mode === 'join'
                    ? 'bg-gold-500 text-black shadow-[0_0_20px_rgba(234,179,8,0.3)]'
                    : 'bg-black/30 text-gold-100/70 hover:bg-gold-500/10'
                    }`}
                >
                  <Play className="w-4 h-4 inline mr-2" />
                  Rejoindre une Partie
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Player Name */}
              {!user && (
                <div className="space-y-2">
                  <label className="block text-gold-100 font-semibold font-serif tracking-wide">
                    Nom du Joueur *
                  </label>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Entrez votre nom..."
                    className={`w-full px-4 py-3 rounded-xl bg-neutral-900/50 border text-gold-100 placeholder-gold-100/30 focus:outline-none focus:ring-1 transition-all ${errors.playerName
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-gold-500/30 focus:border-gold-500 focus:ring-gold-500'
                      }`}
                    maxLength={20}
                  />
                  {errors.playerName && (
                    <p className="text-red-400 text-sm mt-1">{errors.playerName}</p>
                  )}
                </div>
              )}

              {/* Room Code (Join Mode) */}
              {mode === 'join' && (
                <div className="space-y-2">
                  <label className="block text-gold-100 font-semibold font-serif tracking-wide">
                    Code de Partie *
                  </label>
                  <input
                    type="text"
                    value={roomCode}
                    onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                    placeholder="Ex: ABC123"
                    className={`w-full px-4 py-3 rounded-xl bg-neutral-900/50 border text-gold-100 placeholder-gold-100/30 focus:outline-none focus:ring-1 transition-all ${errors.roomCode
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-gold-500/30 focus:border-gold-500 focus:ring-gold-500'
                      }`}
                    maxLength={6}
                  />
                  {errors.roomCode && (
                    <p className="text-red-400 text-sm mt-1">{errors.roomCode}</p>
                  )}
                </div>
              )}

              {/* Create Mode Fields */}
              {mode === 'create' && (
                <>
                  <div className="space-y-2">
                    <label className="block text-gold-100 font-semibold font-serif tracking-wide">
                      Nombre de Joueurs Maximum
                    </label>
                    <select
                      value={maxPlayers}
                      onChange={(e) => setMaxPlayers(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-xl bg-neutral-900/50 border border-gold-500/30 text-gold-100 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all cursor-pointer"
                    >
                      <option value={2} className="bg-neutral-900">2 Joueurs (Duel)</option>
                      <option value={3} className="bg-neutral-900">3 Joueurs (Trio)</option>
                      <option value={4} className="bg-neutral-900">4 Joueurs (Complet)</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-gold-100 font-semibold font-serif tracking-wide">
                        Pack de Jeu
                      </label>
                      {user && (
                        <a href="/dashboard" className="text-xs text-gold-400 hover:text-gold-300 hover:underline">
                          Gérer mes packs
                        </a>
                      )}
                    </div>
                    <select
                      value={selectedPackId}
                      onChange={(e) => setSelectedPackId(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-neutral-900/50 border border-gold-500/30 text-gold-100 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all cursor-pointer"
                    >
                      <option value="" className="bg-neutral-900" disabled>Sélectionner un pack...</option>
                      <optgroup label="Réminiscence Classique (Officiels)" className="bg-neutral-900 text-gold-500 font-bold">
                        {packs.officials.map(pack => (
                          <option key={pack._id} value={pack._id} className="bg-neutral-900 text-gold-100 font-normal">
                            {pack.name} {pack.isAdult ? '(18+)' : ''}
                          </option>
                        ))}
                      </optgroup>
                      {user && (
                        <optgroup label="Mes Créations" className="bg-neutral-900 text-gold-500 font-bold">
                          {packs.mine.length > 0 ? (
                            packs.mine.map(pack => (
                              <option key={pack._id} value={pack._id} className="bg-neutral-900 text-gold-100 font-normal">
                                {pack.name} {pack.isAdult ? '(18+)' : ''}
                              </option>
                            ))
                          ) : (
                            <option disabled className="text-gray-500">Aucun pack créé</option>
                          )}
                        </optgroup>
                      )}
                    </select>
                    {getSelectedPack()?.isAdult && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1 font-bold">
                        ⚠️ Ce pack contient du contenu adulte.
                      </p>
                    )}
                    {getSelectedPack()?.description && (
                      <p className="text-gold-100/50 text-xs mt-1 italic">
                        {getSelectedPack()?.description}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!playerName.trim() || (mode === 'join' && !roomCode.trim()) || isSubmitting}
                className={`w-full py-4 font-bold rounded-xl transition-all duration-300 shadow-lg text-lg ${isSubmitting
                  ? 'bg-neutral-800 cursor-wait text-gold-100/50'
                  : 'bg-gold-500 text-black hover:bg-gold-400 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(234,179,8,0.4)] disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
              >
                {mode === 'create' ? (
                  <>
                    <Users className="w-5 h-5 inline mr-2" />
                    Créer la Partie
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 inline mr-2" />
                    Rejoindre la Partie
                  </>
                )}
              </button>
            </form>

            {/* Info */}
            <div className="mt-8 p-4 bg-gold-500/5 rounded-xl border border-gold-500/20">
              <h3 className="text-gold-400 font-semibold mb-2 flex items-center gap-2">
                <span className="text-lg">ℹ️</span> Information
              </h3>
              <p className="text-gold-100/70 text-sm leading-relaxed">
                {mode === 'create'
                  ? "En créant une partie, vous devenez l'hôte et pourrez inviter d'autres joueurs avec le code généré."
                  : "Demandez le code de partie à votre hôte pour rejoindre sa partie en cours de création."
                }
              </p>
            </div>
          </motion.div>
        </main>
      </div>
    </VintageLayout>
  )
}

export default GameSetup
